export interface CepAddress {
  zipCode: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface CepLookupOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 4000;
const VIACEP_URL = "https://viacep.com.br/ws";
const BRASILAPI_URL = "https://brasilapi.com.br/api/cep/v1";

export function normalizeCep(value: string): string {
  return value.replace(/\D/g, "").slice(0, 8);
}

export function formatCep(value: string): string {
  const digits = normalizeCep(value);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

export function isCompleteCep(value: string): boolean {
  return normalizeCep(value).length === 8;
}

function abortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

async function requestJson(url: string, options: CepLookupOptions): Promise<{ status: number; data: unknown }> {
  if (options.signal?.aborted) throw abortError();

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  options.signal?.addEventListener("abort", onAbort, { once: true });

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return { status: response.status, data: null };
    return { status: response.status, data: await response.json() };
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener("abort", onAbort);
  }
}

async function lookupViaCep(cep: string, options: CepLookupOptions): Promise<CepAddress | null> {
  const { status, data } = await requestJson(`${VIACEP_URL}/${cep}/json/`, options);
  if (status === 400 || status === 404) return null;
  if (status >= 500 || status === 429) throw new Error(`ViaCEP unavailable (${status})`);
  if (!data || typeof data !== "object") return null;

  const payload = data as Record<string, unknown>;
  if (payload.erro === true || payload.erro === "true") return null;

  return {
    zipCode: cep,
    street: stringValue(payload.logradouro),
    neighborhood: stringValue(payload.bairro),
    city: stringValue(payload.localidade),
    state: stringValue(payload.uf).toUpperCase(),
  };
}

async function lookupBrasilApi(cep: string, options: CepLookupOptions): Promise<CepAddress | null> {
  const { status, data } = await requestJson(`${BRASILAPI_URL}/${cep}`, options);
  if (status === 400 || status === 404) return null;
  if (status >= 500 || status === 429) throw new Error(`BrasilAPI unavailable (${status})`);
  if (!data || typeof data !== "object") return null;

  const payload = data as Record<string, unknown>;
  return {
    zipCode: cep,
    street: stringValue(payload.street),
    neighborhood: stringValue(payload.neighborhood),
    city: stringValue(payload.city),
    state: stringValue(payload.state).toUpperCase(),
  };
}

/**
 * Consulta um CEP no ViaCEP com fallback para a BrasilAPI. Retorna `null` quando
 * nenhum provedor conhece o CEP; lança erro quando todos falham por rede/timeout.
 */
export async function fetchAddressByCep(value: string, options: CepLookupOptions = {}): Promise<CepAddress | null> {
  const cep = normalizeCep(value);
  if (cep.length !== 8) return null;

  const providers = [lookupViaCep, lookupBrasilApi];
  let notFound = false;
  let lastError: unknown = null;

  for (const provider of providers) {
    try {
      const address = await provider(cep, options);
      if (address) return address;
      notFound = true;
    } catch (error) {
      if (options.signal?.aborted) throw error;
      lastError = error;
    }
  }

  if (lastError && !notFound) throw lastError;
  return null;
}
